import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api';
import { orgPath } from '../nav';
import { useToast } from '../toast';

export interface SkillSummary {
  id: string;
  code: string;
  name: string;
  summary: string;
  status: 'active' | 'disabled';
  objectCount: number;
  grantCount: number;
}

/** Derived from the name, because a code is a handle and nobody should have to invent one twice. */
const codeFrom = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

/**
 * The skills on one Salesforce org.
 *
 * A skill is a named task an agent may run: which objects and fields it may touch, and which records.
 * It belongs to an org rather than to the workspace, because those objects and fields are one org's
 * schema. The same task shape wanted across several orgs is a skill in each, which is what lets them
 * differ where the orgs do — and what stops a field existing in one from being promised in another.
 */
export function Skills() {
  const { tenantId = '', orgId = '' } = useParams<{ tenantId: string; orgId: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [name, setName] = useState('');
  const [summary, setSummary] = useState('');
  const [prompt, setPrompt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  // Under the org in the address, so the menu keeps this org open while its skill is being edited.
  const skillPath = (id: string) => orgPath(tenantId, orgId, `skills/${id}`);

  const load = useCallback(async () => {
    try {
      const page = await api<{ skills: SkillSummary[] }>(`/api/tenants/${tenantId}/orgs/${orgId}/skills`);
      setSkills(page.skills);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the skills');
    } finally {
      setLoaded(true);
    }
  }, [tenantId, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const created = await api<{ id: string }>(`/api/tenants/${tenantId}/orgs/${orgId}/skills`, {
        method: 'POST',
        body: { code: codeFrom(name), name, summary, prompt },
      });
      setName('');
      setSummary('');
      setPrompt('');
      // Straight into the editor. A skill with no objects can do nothing at all, so landing back on the
      // list invites an admin to think the useful part is done when none of it is.
      navigate(skillPath(created.id));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create that skill');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <p className="small muted" style={{ marginTop: 0 }}>
        An agent with an Omni MCP grant on this org is offered every active skill here and nothing else, so a
        skill is where an agent gets narrower than the Salesforce user it connects as.
      </p>

      {error && <p className="alert">{error}</p>}

      <div className="card flush">
        <table>
          <thead>
            <tr>
              <th>Skill</th>
              <th>Code</th>
              <th>Objects</th>
              <th>Grants</th>
              <th>Status</th>
              <th className="actions-cell"></th>
            </tr>
          </thead>
          <tbody>
            {!loaded && (
              <tr>
                <td colSpan={6} className="empty">
                  Loading…
                </td>
              </tr>
            )}
            {loaded && !skills.length && (
              <tr>
                <td colSpan={6} className="empty">
                  No skills yet. Make one below and add the objects and fields it needs; anyone with an Omni
                  MCP grant on this org can then run it. A skill names this org’s objects and fields, so it
                  belongs to this org and not to the workspace.
                </td>
              </tr>
            )}
            {skills.map((s) => (
              <tr key={s.id}>
                <td>
                  <Link to={skillPath(s.id)}>
                    <strong>{s.name}</strong>
                  </Link>
                  {s.summary && <div className="small muted">{s.summary}</div>}
                </td>
                <td className="small mono">{s.code}</td>
                <td>{s.objectCount}</td>
                <td>{s.grantCount}</td>
                <td>
                  <span className={`pill ${s.status === 'active' ? 'on' : 'off'}`}>
                    {s.status === 'active' ? 'active' : 'disabled'}
                  </span>
                </td>
                <td className="actions-cell">
                  <Link className="button secondary" to={skillPath(s.id)}>
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form className="card" onSubmit={create}>
        <h2>Create a skill</h2>
        <label>
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required />
        </label>
        <p className="small muted">
          The code will be <code>{codeFrom(name) || '…'}</code>. An agent quotes it on every call.
        </p>
        <label>
          Summary
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={300}
            required
            placeholder="What this skill is for, in one line"
          />
        </label>
        <p className="small muted">
          This is what an agent reads when it is choosing, so write it as the answer to “is this the right
          one for what I was asked?”
        </p>
        <label>
          How to do it
          <textarea
            rows={4}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="What the agent should actually do once it has picked this skill. You can fill this in later."
          />
        </label>
        <p className="small muted">
          The objects and fields you choose next are what the agent <em>may</em> touch. This is what you
          want it to do with them.
        </p>
        <button type="submit" className="block" disabled={busy || !name.trim() || !summary.trim()}>
          {busy ? 'Creating…' : 'Create and choose its objects'}
        </button>
      </form>
    </>
  );
}
